<template>
    <div class="pa-4">
        <div v-if="carregando && !meta.total" class="text-center py-10">
            <v-progress-circular indeterminate color="primary"/>
        </div>

        <template v-else>
            <v-card outlined class="pa-4 mb-4">
                <div class="d-flex flex-wrap align-baseline" style="gap: 10px;">
                    <span class="text-h4 font-weight-bold">{{ meta.migradas || 0 }} de {{ meta.total || 0 }}</span>
                    <span class="subtitle-1 grey--text text--darken-2">empresas migradas · {{ pct(meta.migradas, meta.total) }}%</span>
                </div>
                <v-progress-linear
                    :value="pct(meta.migradas, meta.total)"
                    height="10"
                    rounded
                    color="primary"
                    background-color="grey lighten-3"
                    class="my-3"
                />
                <v-row dense>
                    <v-col v-for="c in cenarios" :key="c.chave" cols="12" md="6">
                        <div class="d-flex justify-space-between body-2">
                            <span>{{ c.texto }}</span>
                            <span class="font-weight-medium">{{ c.migradas }} de {{ c.total }}</span>
                        </div>
                        <v-progress-linear
                            :value="pct(c.migradas, c.total)"
                            height="6"
                            rounded
                            color="primary"
                            background-color="grey lighten-3"
                            class="mt-1"
                        />
                    </v-col>
                </v-row>
                <v-alert v-if="meta.fora_da_campanha" dense text type="warning" class="mt-3 mb-0 body-2">
                    {{ meta.fora_da_campanha }} das migradas conectaram pelo menu, fora de onda, sem aceite registrado.
                </v-alert>
            </v-card>

            <v-row dense class="mb-2">
                <v-col v-for="c in cenarios" :key="`funil-${c.chave}`" cols="12" md="6">
                    <v-card outlined class="pa-4" style="height: 100%;">
                        <div class="text-overline text-center mb-2">Funil · {{ c.texto }}</div>
                        <div v-for="e in c.funil" :key="e.etapa" class="funil-linha">
                            <span class="body-2">{{ e.etapa }}</span>
                            <div class="funil-trilho" :title="`${e.etapa}: ${e.valor}`">
                                <div class="funil-barra" :style="{ width: `${e.pct}%` }"/>
                            </div>
                            <span class="body-2 text-right"><b>{{ e.valor }}</b> · {{ e.pct }}%</span>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense class="mb-2">
                <v-col cols="12" md="8">
                    <v-card outlined class="pa-4" style="height: 100%;">
                        <div class="text-overline text-center mb-2">Conexões por dia</div>
                        <div v-if="!dias.length" class="body-2 grey--text text-center py-6">Nenhuma conexão ainda.</div>
                        <div v-else class="dias-rolagem">
                            <div class="dias-grade" :style="{ gridTemplateColumns: `repeat(${dias.length}, minmax(28px, 1fr))` }">
                                <div v-for="d in dias" :key="d.dia" class="dia" :title="`${d.rotulo}: ${d.total} conexões`">
                                    <span class="caption font-weight-medium">{{ d.total || '' }}</span>
                                    <div class="dia-barra" :style="{ height: `${d.altura}px` }"/>
                                </div>
                            </div>
                            <div class="dias-grade dias-rotulos" :style="{ gridTemplateColumns: `repeat(${dias.length}, minmax(28px, 1fr))` }">
                                <div v-for="d in dias" :key="`r-${d.dia}`" class="caption grey--text text--darken-1 text-center">
                                    {{ d.rotulo }}
                                    <div v-if="d.prazo" class="font-weight-bold grey--text text--darken-3">prazo</div>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card outlined class="pa-4 mb-2">
                        <div class="text-overline">Volume PIX já no split</div>
                        <div class="text-h5">{{ pct(receita.volume_migrado, receita.volume_total) }}%</div>
                        <div class="caption grey--text text--darken-1">
                            {{ reais(receita.volume_migrado) }} de {{ reais(receita.volume_total) }} de PIX em 30 dias, medido antes da migração
                        </div>
                    </v-card>
                    <v-card outlined class="pa-4 mb-2">
                        <div class="text-overline">Taxa gerada desde a migração</div>
                        <div class="text-h5">{{ reais(taxaTotal) }}</div>
                        <div class="caption grey--text text--darken-1">
                            PF → PJ: {{ reais(taxa.reconexao_pj) }} · chave de acesso: {{ reais(taxa.legado_oauth) }}
                        </div>
                    </v-card>
                    <v-card outlined class="pa-4">
                        <div class="text-overline">Cancelamentos entre as migradas</div>
                        <div class="d-flex align-center" style="gap: 8px;">
                            <v-icon :color="cancelamentos >= 2 ? 'red darken-2' : 'green darken-2'">
                                {{ cancelamentos >= 2 ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
                            </v-icon>
                            <span class="text-h5">{{ cancelamentos }}</span>
                            <span class="body-2">{{ cancelamentos >= 2 ? 'avaliar pausa da onda' : 'dentro do critério' }}</span>
                        </div>
                        <div class="caption grey--text text--darken-1">Pausar a onda com 2 cancelamentos atribuídos à taxa</div>
                    </v-card>
                </v-col>
            </v-row>

            <v-card outlined class="pa-4 mb-4">
                <div class="text-overline text-center mb-2">Por onda</div>
                <v-data-table
                    :headers="headersOndas"
                    :items="ondas"
                    :mobileBreakpoint="0"
                    hide-default-footer
                    disable-pagination
                    dense
                    no-data-text="Nenhuma onda liberada ainda"
                >
                    <template v-slot:item.onda="{ item }">
                        <span class="font-weight-medium">{{ item.onda }}</span>
                        <span class="caption grey--text"> · {{ formatDate(item.liberada_em, 'DD/MM') }}</span>
                    </template>
                </v-data-table>
            </v-card>

            <v-card outlined class="pa-4">
                <div class="text-overline text-center mb-2">Precisam de contato ({{ contato.length }})</div>
                <v-data-table
                    :headers="headersContato"
                    :items="contato"
                    :mobileBreakpoint="0"
                    :items-per-page="20"
                    dense
                    no-data-text="Ninguém precisa de contato agora"
                >
                    <template v-slot:item.motivo="{ item }">
                        <v-icon small color="orange darken-3" class="mr-1">mdi-alert-outline</v-icon>
                        {{ motivoContato(item.motivo) }}
                    </template>
                    <template v-slot:item.volume_pix_30d_antes="{ item }">{{ reais(item.volume_pix_30d_antes) }}</template>
                    <template v-slot:item.desde="{ item }">{{ formatDate(item.desde) || '—' }}</template>
                    <template v-slot:item.acao="{ item }">
                        <v-btn small text color="primary" @click="$emit('ver-empresa', item.empresa_id)">Ver empresa</v-btn>
                    </template>
                </v-data-table>
            </v-card>
        </template>
    </div>
</template>

<script>
import { cenario, motivoContato, reais } from './situacoes';

export default {
    name: 'TabProgresso',

    props: {
        resumo: { type: Object, default: () => ({}) },
        carregando: { type: Boolean, default: false },
    },

    data: () => ({
        headersOndas: [
            { text: 'Onda', value: 'onda', sortable: false },
            { text: 'Liberadas', value: 'liberadas', sortable: false },
            { text: 'Conectadas', value: 'conectadas', sortable: false },
            { text: 'Conta diferente', value: 'conta_diferente', sortable: false },
            { text: 'Sem referência', value: 'sem_referencia', sortable: false },
            { text: 'Adiamentos médios', value: 'adiamentos_medios', sortable: false },
            { text: 'Pausadas', value: 'pausadas', sortable: false },
            { text: 'Desfeitas', value: 'desfeitas', sortable: false },
        ],
        headersContato: [
            { text: 'Empresa', value: 'fantasia', sortable: false },
            { text: 'Motivo', value: 'motivo', sortable: false },
            { text: 'PIX em 30 dias', value: 'volume_pix_30d_antes', sortable: false },
            { text: 'Desde', value: 'desde', sortable: false },
            { text: '', value: 'acao', sortable: false, align: 'end' },
        ],
    }),

    computed: {
        meta() {
            return this.resumo.meta || {};
        },

        receita() {
            return this.resumo.receita || {};
        },

        taxa() {
            return this.receita.taxa_desde_migracao || {};
        },

        taxaTotal() {
            return (this.taxa.reconexao_pj || 0) + (this.taxa.legado_oauth || 0);
        },

        cancelamentos() {
            return this.resumo.cancelamentos || 0;
        },

        ondas() {
            return this.resumo.ondas || [];
        },

        contato() {
            return this.resumo.contato || [];
        },

        cenarios() {
            const porCenario = this.meta.por_cenario || {};
            const funil = this.resumo.funil || {};

            return ['reconexao_pj', 'legado_oauth'].map(chave => {
                const f = funil[chave] || {};
                const total = f.candidatas || 0;
                const etapa = (nome, valor) => ({ etapa: nome, valor: valor || 0, pct: this.pct(valor, total) });

                return {
                    chave,
                    texto: chave === 'reconexao_pj' ? 'PF → PJ' : 'Chave de acesso → Mercado Pago',
                    total: (porCenario[chave] || {}).total || 0,
                    migradas: (porCenario[chave] || {}).migradas || 0,
                    funil: [
                        etapa('Candidatas', f.candidatas),
                        etapa('Liberadas', f.liberadas),
                        etapa('Viram a tela', f.viram),
                        etapa('Aceitaram', f.aceitaram),
                        etapa('Iniciaram a conexão', f.iniciaram),
                        etapa('Conectaram', f.conectaram),
                    ],
                };
            });
        },

        // Do primeiro dia com conexão até o prazo (ou hoje), com os dias vazios também.
        dias() {
            const conexoes = this.resumo.conexoes_por_dia || [];
            const prazo = this.resumo.prazo && this.resumo.prazo.em ? this.moment(this.resumo.prazo.em) : null;

            if (!conexoes.length) {
                return [];
            }

            const porDia = {};
            conexoes.forEach(c => (porDia[c.dia] = +c.total));

            const inicio = this.moment(conexoes[0].dia);
            let fim = this.moment().startOf('day');
            if (prazo && prazo.isAfter(fim) && prazo.diff(inicio, 'days') <= 45) {
                fim = prazo.clone().startOf('day');
            }

            const max = Math.max(...Object.values(porDia), 1);
            const lista = [];

            for (const d = inicio.clone(); d.isSameOrBefore(fim, 'day'); d.add(1, 'day')) {
                const chave = d.format('YYYY-MM-DD');
                const total = porDia[chave] || 0;

                lista.push({
                    dia: chave,
                    rotulo: d.format('DD/MM'),
                    total,
                    altura: total ? Math.max(4, Math.round((total / max) * 140)) : 2,
                    prazo: !!prazo && d.isSame(prazo, 'day'),
                });
            }

            return lista;
        },
    },

    methods: {
        cenario,
        motivoContato,
        reais,

        pct(parte, total) {
            return total ? Math.round(((parte || 0) / total) * 100) : 0;
        },
    },
};
</script>

<style scoped>
.funil-linha {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr) 80px;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
}

.funil-trilho {
    height: 14px;
    background: #f0f3f7;
    border-radius: 0 4px 4px 0;
}

.funil-barra {
    height: 100%;
    background: #1976d2;
    border-radius: 0 4px 4px 0;
}

.dias-rolagem {
    overflow-x: auto;
}

.dias-grade {
    display: grid;
    gap: 6px;
    min-width: 100%;
}

.dias-grade:first-child {
    height: 170px;
    align-items: end;
    border-bottom: 1px solid #c9d2dd;
}

.dias-rotulos {
    padding-top: 6px;
}

.dia {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
    gap: 4px;
}

.dia-barra {
    width: 60%;
    background: #1976d2;
    border-radius: 4px 4px 0 0;
}

@media (max-width: 600px) {
    .funil-linha {
        grid-template-columns: 110px minmax(0, 1fr) 72px;
    }
}
</style>
